import { Row, Col, TreeProps } from "antd";
import useAllAssets from "../../hooks/useAllAssets";
import { FormState } from "./Aspects";
import PreviewComp from "./Preview";
import React, { useEffect, useState } from "react";
import DrawerComp from "../../components/Drawer";
import TreeComp from "../../components/Tree";
import useProjects from "../../hooks/useProjects";
import useAssetsTypes from "../../hooks/useAssetsTypes";
import usePagination from "../../hooks/usePagination";
import { IAsset } from "../../models/Assets";
import { SUCCESS_PANEL } from "./DefaultPanels";
import PMVHooks from "./PMVHooks";
import { useSubscription } from '@apollo/client';
import { OnLastPMVMessage } from "../../graphql/subscriptions/pmv_status";
import CustomLayout from "components/Layout";

type PMVType = {
    formState: FormState[];
}

const PMV: React.FC<PMVType> = ({ formState }) => {
    const [currentAspectIndex, setCurrentAspectIndex] = useState<number>(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentAspectIndex((prevIndex) => (prevIndex + 1) % formState.length);
        }, 2000); // Cambiar cada 2 segundos
        return () => clearInterval(interval); // Limpiar el intervalo al desmontar el componente
    }, [formState.length]);

    return (
        <PreviewComp
            currentAspectIndex={currentAspectIndex}
            formState={formState}
        />
    )
}

function hasTwoHyphens(str: string): boolean {
    return str.split('-').length === 3;
}

interface IPMV {
    id: number;
    formState: FormState[];
}

const ListPMV = () => {
    const { getPMVStatus, formStates, hookPMVStatuses, updatePanelState } = PMVHooks();
    const hookAssets = useAllAssets({ pageSize: 1000, type_id: 20 });
    const hookAssetsSelected = usePagination<IAsset>({ pageSize: 1000 });
    const hookProjects = useProjects({ package_id: 17, pageSize: 10 });
    const hookAssetsTypes = useAssetsTypes({ package_id: 17, pageSize: 20 });
    const [checkedKeys, setCheckedKeys] = useState<TreeProps['checkedKeys']>([]);

    const { data: subscriptionData } = useSubscription(OnLastPMVMessage);

    useEffect(() => {
        // Actualizar panel si se recibe un nuevo mensaje
        if (subscriptionData) {
            const pmvId = subscriptionData.its_pmv_statuses[0].pmv_id;
            const pmv = hookAssets.data.find((asset) => asset.id === pmvId);
            if (pmv) {
                const message = subscriptionData.its_pmv_statuses[0].message;
                const isOnline = subscriptionData.its_pmv_statuses[0].is_online;
                updatePanelState(pmvId, message, isOnline);
            }
        }
    }, [subscriptionData]);

    useEffect(() => {
        if (!hookAssets.loading) {
            hookAssetsSelected.setData(hookAssets.data);
            hookAssetsSelected.loadingEnd();
            // Marcar checkedkeys por default
            const allCheckedKeys = hookAssets.data.map((checkedKey) => `${checkedKey.project_id}-${checkedKey.type_id}-${checkedKey.id}`)
            setCheckedKeys(allCheckedKeys);
        }
    }, [hookAssets.loading]);

    useEffect(() => {
        hookAssetsSelected.loadingStart();
        // Asegurarse de que checkedKeys es un array de strings
        const keysArray = Array.isArray(checkedKeys) ? checkedKeys : [];

        const checkedAssets = keysArray
            .filter((checkedKey) => hasTwoHyphens(String(checkedKey))) // Filtra solo los keys con dos guiones
            .map((checkedKey) => String(checkedKey).split('-')[2]);

        // Filtrar los assets basados en checkedAssets
        const filteredAssets = hookAssets.data.filter((asset) =>
            checkedAssets.includes(String(asset.id))
        );
        // Actualizar los datos en hookAssets
        hookAssetsSelected.setData(filteredAssets);
        hookAssetsSelected.loadingEnd();
    }, [checkedKeys, hookAssets.data]); // Añadir todas las dependencias necesarias

    // Obtener el mensaje actual de cada panel 
    useEffect(() => {
        if (!hookAssetsSelected.loading && hookAssetsSelected.data && !hookPMVStatuses.loading) {
            for (let pmv of hookAssetsSelected.data) {
                // Obtener el estado actual del PMV
                getPMVStatus(pmv);
            }
        }
    }, [hookAssetsSelected.data, hookAssetsSelected.loading, hookPMVStatuses.loading]);

    return (
        <CustomLayout>
            <div
                style={{
                    height: '100vh', // Ajusta la altura al tamaño de la ventana
                    padding: '0.5rem', // Espaciado
                    overflow: 'hidden', // Evita el desbordamiento global
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/* Filtros de visualización */}
                <DrawerComp title='Filtros'>
                    <TreeComp
                        hookProjects={hookProjects}
                        hookAssetsTypes={hookAssetsTypes}
                        hookAssets={hookAssetsSelected}
                        checkedKeys={checkedKeys}
                        setCheckedKeys={setCheckedKeys}
                    />
                </DrawerComp>
                <div
                    style={{
                        flex: 1, // Hacer que ocupe todo el espacio disponible
                        overflowY: 'auto', // Habilitar scroll si hay demasiados elementos
                        overflowX: 'auto',
                        padding: '0.2rem',
                        boxSizing: 'border-box',
                    }}
                >
                    <Row gutter={[8, 8]} justify="center">
                        {hookAssetsSelected.data
                            .filter(asset => asset.type_id === 20)
                            .map((asset) => (
                                <Col
                                    key={asset.id}
                                    xs={24}
                                    sm={12}
                                    md={12}
                                    lg={8}
                                    xl={8}
                                    xxl={6}
                                >
                                    <div
                                        style={{
                                            background: '#f5f5f5',
                                            borderRadius: '8px',
                                            padding: '0.5rem',
                                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                                            textAlign: 'center',
                                            minWidth: "350px",
                                            maxWidth: "400px",
                                        }}
                                    >
                                        <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                                            {asset.description}
                                        </p>
                                        <PMV formState={formStates.find(formState => formState.id === asset.id)?.formState || SUCCESS_PANEL} />
                                    </div>
                                </Col>
                            ))}
                    </Row>
                </div>
            </div>
        </CustomLayout>
    );
};

export default ListPMV;
